import {
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
  Button,
  Chip,
  Tabs,
  Tab,
  Popover,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  LinearProgress,
} from "@mui/material";
import { useState, useEffect } from "react";
import { isMobile } from "react-device-detect";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";

const BG_PAGE = "#15151D";
const BG_CARD = "#15151D";
const BORDER = "#2A2A38";
const TEXT_PRIMARY = "#FFFFFF";
const TEXT_MUTED = "#8A8A9A";
const TEXT_LABEL = "#9AA0B4";
const ACCENT_BLUE = "#388ADE";
const ACCENT_GREEN = "#639822";
const ACCENT_ORANGE = "#BA7516";
const ACCENT_RED = "#E34A4A";

// ── Tier card Figma-accurate colors ──
// Tier 1 - High value: light green pastel bg, green label
const TIER1_BG = "#EAF3DE";
const TIER1_LABEL = "#4A7A10";   // darker green for readability on light bg
const TIER1_ICON = "#639822";
const TIER1_TEXT = "#1A2A0A";    // dark text for light bg
const TIER1_MUTED = "#5A6A4A";

// Tier 2 - Good fit: light blue pastel bg, blue label
const TIER2_BG = "#E7F1FB";
const TIER2_LABEL = "#1A5FA0";
const TIER2_ICON = "#388ADE";
const TIER2_TEXT = "#0A1A2A";
const TIER2_MUTED = "#4A5A6A";

// Tier 3 - Low fit: light orange pastel bg, orange label
const TIER3_BG = "#FBEED7";
const TIER3_LABEL = "#8A5010";
const TIER3_ICON = "#BA7516";
const TIER3_TEXT = "#2A1A0A";
const TIER3_MUTED = "#6A5A3A";

// Tier 4 - Unqualified: light red pastel bg, red label
const TIER4_BG = "#FDEAEB";
const TIER4_LABEL = "#C02A2A";
const TIER4_ICON = "#E34A4A";
const TIER4_TEXT = "#2A0A0A";
const TIER4_MUTED = "#6A4A4A";

// KPI card - matches Figma: fill #15151D (same as page bg), stroke #2D2D2D
const KPI_BG = "#15151D";
const KPI_BORDER = "#2D2D2D";

const interFont = "'Inter', sans-serif";

const timeTabs = [
  { label: "7 Days", value: "7days" },
  { label: "30 Days", value: "30days" },
  { label: "90 Days", value: "90days" },
  { label: "1 Year", value: "1year" },
];

// ─────────────────────────────────────────────
// DUMMY DATA
// ─────────────────────────────────────────────
const DUMMY_DATA = {
  total_leads: 14567,
  new_leads: 4800,
  revenue: "$82.5k",
  conversion_rate: "12.4%",
  new_leads_change: "+12.6%",
  conversion_rate_change: "+2.1%",
  lead_quality: {
    tier1: { label: "Tier 1 - High value", count: 480, convert: "82%" },
    tier2: { label: "Tier 2 - Good fit", count: 620, convert: "45%" },
    tier3: { label: "Tier 3 - Low fit", count: 350, convert: "18%" },
    tier4: { label: "Tier 4 - Unqualified", count: 120, convert: "<5%" },
  },
  quality_distribution: {
    t1: 26,
    t2: 39,
    t3: 24,
    t4: 11,
  },
  discovery_source: {
    total: "4,800",
    date: "1 Apr - 30 Apr, 2025",
    bars: [
      { name: "LinkedIn", value: 2400, color: ACCENT_BLUE },
      { name: "Google", value: 3100, color: ACCENT_GREEN },
      { name: "Substack", value: 1150, color: ACCENT_RED },
      { name: "Twitter", value: 980, color: ACCENT_ORANGE },
      { name: "Other", value: 670, color: "#6B6B7A" },
    ],
  },
  source_breakdown: [
    { source: "LinkedIn", color: ACCENT_BLUE, leads: 2400, tier1: 620, tier2: 1050, tier3: 420, tier4: 50 },
    { source: "Google", color: ACCENT_GREEN, leads: 3100, tier1: 980, tier2: 1240, tier3: 360, tier4: 60 },
    { source: "Substack", color: ACCENT_RED, leads: 1150, tier1: 320, tier2: 420, tier3: 189, tier4: 67 },
    { source: "Twitter", color: ACCENT_ORANGE, leads: 980, tier1: 180, tier2: 240, tier3: 60, tier4: 32 },
    { source: "Other", color: "#6B6B7A", leads: 670, tier1: 98, tier2: 230, tier3: 39, tier4: 34 },
  ],
  email_performs: {
    sent: "9,600",
    opened: "3,200",
    clicked: "14%",
    clicked_badge: "1400 Clicked",
    performance: "2x",
    performance_label: "",
  },
  linkedin_performs: {
    sent: "2,500",
    replied: "170",
    clicked: "6%",
    performance: "Low Response Rate",
    performance_label: "Low Sessions",   
  },
};

// ── KPI Card — Figma: bg #15151D, border #2D2D2D ──
function KpiCard({
  title,
  value,
  subtitle,
  badge,
  badgeColor,
}: {
  title: string;
  value: string;
  subtitle: string;
  badge?: string;
  badgeColor?: string;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: "20px 24px",
        borderRadius: "12px",
        background: KPI_BG,
        border: `1px solid ${KPI_BORDER}`,
        color: TEXT_PRIMARY,
        height: "100%",
      }}
    >
      <Typography fontFamily={interFont} fontSize={13} color={TEXT_MUTED} fontWeight={500} mb={1}>
        {title}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography fontFamily={interFont} fontSize={28} fontWeight={700} letterSpacing="-0.02em">
          {value}
        </Typography>
        {badge && (
          <Chip
            label={badge}
            size="small"
            sx={{
              height: 20,
              fontSize: 11,
              fontFamily: interFont,
              fontWeight: 700,
              color: badgeColor || "#32d583",
              background: badgeColor
                ? `${badgeColor}22`
                : "rgba(50,213,131,0.15)",
              borderRadius: "6px",
            }}
          />
        )}
      </Stack>
      <Typography fontFamily={interFont} fontSize={12} color={TEXT_MUTED} mt={0.5}>
        {subtitle}
      </Typography>
    </Paper>
  );
}

// ── Lead Quality Tier Card — Figma-accurate light pastel ──
function TierCard({
  label,
  count,
  convert,
  bgColor,
  labelColor,
  iconColor,
  textColor,
  mutedColor,
}: {
  label: string;
  count: number;
  convert: string;
  bgColor: string;
  labelColor: string;
  iconColor: string;
  textColor: string;
  mutedColor: string;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: "16px 20px",
        borderRadius: "12px",
        background: bgColor,
        border: `1px solid #2D2D2D`,
        color: textColor,
        height: "100%",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography fontFamily={interFont} fontSize={12} color={labelColor} fontWeight={600}>
          {label}
        </Typography>
        <InfoOutlinedIcon sx={{ fontSize: 14, color: iconColor }} />
      </Stack>
      <Typography fontFamily={interFont} fontSize={28} fontWeight={700} letterSpacing="-0.02em" color={textColor}>
        {count}
      </Typography>
      <Typography fontFamily={interFont} fontSize={12} color={mutedColor} mt={0.5}>
        Convert: {convert}
      </Typography>
    </Paper>
  );
}

// ── Bar Chart (Discovery Source) ──
function BarChart({ bars, total, date }: { bars: typeof DUMMY_DATA["discovery_source"]["bars"]; total: string; date: string }) {
  const maxVal = Math.max(...bars.map((b) => b.value));
  return (
    <Box>
      <Typography fontFamily={interFont} fontSize={13} fontWeight={600} color={TEXT_PRIMARY}>
        Total Leads {total}
      </Typography>
      <Typography fontFamily={interFont} fontSize={11} color={TEXT_MUTED} mb={2}>
        {date}
      </Typography>
      <Stack direction="row" alignItems="flex-end" spacing={1.5} sx={{ height: 120 }}>
        {bars.map((bar) => (
          <Stack key={bar.name} alignItems="center" spacing={0.5} flex={1}>
            <Box
              sx={{
                width: "100%",
                maxWidth: 40,
                height: `${(bar.value / maxVal) * 100}px`,
                background: bar.color,
                borderRadius: "4px 4px 0 0",
                transition: "height 0.4s ease",
              }}
            />
          </Stack>
        ))}
      </Stack>
      <Stack direction="row" spacing={1.5} mt={1}>
        {bars.map((bar) => (
          <Stack key={bar.name} alignItems="center" flex={1}>
            <Typography fontFamily={interFont} fontSize={10} color={TEXT_MUTED} textAlign="center">
              {bar.name}
            </Typography>
          </Stack>
        ))}
      </Stack>
      {/* Legend */}
      <Stack direction="row" flexWrap="wrap" gap={1} mt={1.5}>
        {bars.map((bar) => (
          <Stack key={bar.name} direction="row" alignItems="center" spacing={0.5}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: bar.color }} />
            <Typography fontFamily={interFont} fontSize={11} color={TEXT_MUTED}>
              {bar.name} {bar.value.toLocaleString()}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

// ── Performs Card ──
function PerformsCard({
  icon,
  title,
  rows,
  badgeText,
  badgeColor,
  badgeBg,
  performanceLabel,
  performanceBadge,
  performanceBadgeColor,
}: {
  icon: React.ReactNode;
  title: string;
  rows: { label: string; value: string }[];
  badgeText?: string;
  badgeColor?: string;
  badgeBg?: string;
  performanceLabel: string;
  performanceBadge: string;
  performanceBadgeColor?: string;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: "20px 24px",
        borderRadius: "12px",
        background: BG_CARD,
        border: `1px solid ${BORDER}`,
        color: TEXT_PRIMARY,
        height: "100%",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <Box sx={{ color: TEXT_MUTED, display: "flex" }}>{icon}</Box>
        <Typography fontFamily={interFont} fontSize={14} fontWeight={600}>
          {title}
        </Typography>
      </Stack>
      <Grid container spacing={2}>
        {rows.map((r, idx) => (
          <Grid item xs={6} key={idx}>
            <Typography fontFamily={interFont} fontSize={12} color={TEXT_MUTED} mb={0.3}>
              {r.label}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography fontFamily={interFont} fontSize={22} fontWeight={700}>
                {r.value}
              </Typography>
              {idx === 2 && badgeText && (
                <Chip
                  label={badgeText}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: 10,
                    fontFamily: interFont,
                    fontWeight: 600,
                    color: badgeColor || ACCENT_BLUE,
                    background: badgeBg || `${badgeColor || ACCENT_BLUE}22`,
                    borderRadius: "5px",
                  }}
                />
              )}
            </Stack>
          </Grid>
        ))}
        <Grid item xs={6}>
          <Typography fontFamily={interFont} fontSize={12} color={TEXT_MUTED} mb={0.3}>
            Performance
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography fontFamily={interFont} fontSize={22} fontWeight={700}>
              {performanceLabel}
            </Typography>
            {performanceBadge && (
              <Chip
                label={performanceBadge}
                size="small"
                sx={{
                  height: 18,
                  fontSize: 10,
                  fontFamily: interFont,
                  fontWeight: 600,
                  color: performanceBadgeColor || ACCENT_RED,
                  background: `${performanceBadgeColor || ACCENT_RED}22`,
                  borderRadius: "5px",
                }}
              />
            )}
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default function CustomerNewAcquisition() {
  const [tabValue, setTabValue] = useState(0);
  const [filter, setFilter] = useState("7days");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs("2025-08-01"));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs("2025-08-30"));
  const open = Boolean(anchorEl);
  const data = DUMMY_DATA;

  const { t1, t2, t3, t4 } = data.quality_distribution;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        fontFamily: interFont,
        p: isMobile ? 2 : 3,
      }}
    >
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography fontFamily={interFont} fontSize={22} fontWeight={700} letterSpacing="-0.02em">
            Customer Acquisition
          </Typography>
          <Typography fontFamily={interFont} fontSize={13} color={TEXT_MUTED}>
            Welcome back, Rohan.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
          sx={{
            background: "linear-gradient(90deg, #0168B5 0%, #4BA5EB 100%)",
            textTransform: "none",
            borderRadius: "8px",
            fontSize: "13px",
            fontWeight: 500,
            whiteSpace: "nowrap",
            flexShrink: 0,
            ml: 2,
            boxShadow: "none",
            color: "#fff",
            "&:hover": {
              background: "linear-gradient(90deg, #0158a0 0%, #3a94da 100%)",
              boxShadow: "none",
            },
          }}
        >
          Export Report
        </Button>
      </Stack>

      {/* High-level KPIs */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography fontFamily={interFont} fontSize={15} fontWeight={600}>
          High-level KPIs
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography fontFamily={interFont} fontSize={12} color={TEXT_MUTED}>
            Select Date range:
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Button
              size="small"
              startIcon={<CalendarTodayOutlinedIcon sx={{ fontSize: 13 }} />}
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                textTransform: "none",
                fontFamily: interFont,
                fontSize: 12,
                color: TEXT_PRIMARY,
                background: BG_CARD,
                borderRadius: "6px",
                px: 1.5,
                border: `1px solid ${BORDER}`,
                whiteSpace: "nowrap",
              }}
            >
              {startDate?.format("MMM D")} - {endDate?.format("MMM D, YYYY")}
            </Button>
            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <Box sx={{ p: 2, display: "flex", gap: 2, background: BG_CARD, border: `1px solid ${BORDER}` }}>
                <DatePicker label="From" value={startDate} onChange={setStartDate} />
                <DatePicker label="To" value={endDate} minDate={startDate || undefined} onChange={setEndDate} />
              </Box>
            </Popover>
          </LocalizationProvider>
        </Stack>
      </Stack>

      {/* KPI Cards — bg #15151D, border #2D2D2D per Figma */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={3}>
          <KpiCard
            title="Total Leads"
            value="14,567"
            subtitle="All-time"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <KpiCard
            title="New Leads"
            value="4,800"
            subtitle="vs prior period"
            badge="+12.6%"
            badgeColor="#32d583"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <KpiCard
            title="Revenue"
            value="$82.5k"
            subtitle="207 new customers"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <KpiCard
            title="Conversion rate"
            value="12.4%"
            subtitle="vs last 30 days"
            badge="+2.1%"
            badgeColor="#32d583"
          />
        </Grid>
      </Grid>

      {/* Lead Quality */}
      <Typography fontFamily={interFont} fontSize={15} fontWeight={600} mb={1.5}>
        Lead Quality
      </Typography>
      <Grid container spacing={2} mb={2.5}>
        {/* Tier 1 — light green pastel (#EAF3DE) from Figma */}
        <Grid item xs={12} md={3}>
          <TierCard
            label="Tier 1 - High value"
            count={data.lead_quality.tier1.count}
            convert={data.lead_quality.tier1.convert}
            bgColor={TIER1_BG}
            labelColor={TIER1_LABEL}
            iconColor={TIER1_ICON}
            textColor={TIER1_TEXT}
            mutedColor={TIER1_MUTED}
          />
        </Grid>
        {/* Tier 2 — light blue pastel (#E7F1FB) from Figma */}
        <Grid item xs={12} md={3}>
          <TierCard
            label="Tier 2 - Good fit"
            count={data.lead_quality.tier2.count}
            convert={data.lead_quality.tier2.convert}
            bgColor={TIER2_BG}
            labelColor={TIER2_LABEL}
            iconColor={TIER2_ICON}
            textColor={TIER2_TEXT}
            mutedColor={TIER2_MUTED}
          />
        </Grid>
        {/* Tier 3 — light orange pastel (#FBEED7) from Figma */}
        <Grid item xs={12} md={3}>
          <TierCard
            label="Tier 3 - Low fit"
            count={data.lead_quality.tier3.count}
            convert={data.lead_quality.tier3.convert}
            bgColor={TIER3_BG}
            labelColor={TIER3_LABEL}
            iconColor={TIER3_ICON}
            textColor={TIER3_TEXT}
            mutedColor={TIER3_MUTED}
          />
        </Grid>
        {/* Tier 4 — light red pastel (#FDEAEB) from Figma */}
        <Grid item xs={12} md={3}>
          <TierCard
            label="Tier 4 - Unqualified"
            count={data.lead_quality.tier4.count}
            convert={data.lead_quality.tier4.convert}
            bgColor={TIER4_BG}
            labelColor={TIER4_LABEL}
            iconColor={TIER4_ICON}
            textColor={TIER4_TEXT}
            mutedColor={TIER4_MUTED}
          />
        </Grid>
      </Grid>

      {/* Quality Distribution */}
      <Paper
        elevation={0}
        sx={{
          p: "16px 20px",
          borderRadius: "12px",
          background: BG_CARD,
          border: `1px solid ${BORDER}`,
          mb: 3,
        }}
      >
        <Typography fontFamily={interFont} fontSize={13} color={TEXT_MUTED} mb={1.5}>
          Quality distribution across 8,300 discovered leads
        </Typography>
        {/* Stacked bar */}
        <Stack direction="row" sx={{ borderRadius: "6px", overflow: "hidden", height: 28, mb: 1.5 }}>
          <Box sx={{ width: `${t1}%`, background: ACCENT_GREEN, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography fontFamily={interFont} fontSize={11} fontWeight={600} color="#fff">T1</Typography>
          </Box>
          <Box sx={{ width: `${t2}%`, background: ACCENT_BLUE, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography fontFamily={interFont} fontSize={11} fontWeight={600} color="#fff">T2</Typography>
          </Box>
          <Box sx={{ width: `${t3}%`, background: ACCENT_ORANGE, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography fontFamily={interFont} fontSize={11} fontWeight={600} color="#fff">T3</Typography>
          </Box>
          <Box sx={{ width: `${t4}%`, background: ACCENT_RED, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography fontFamily={interFont} fontSize={11} fontWeight={600} color="#fff">T4</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={2.5} flexWrap="wrap" gap={1}>
          {[
            { color: ACCENT_GREEN, label: `High value (${t1}%)` },
            { color: ACCENT_BLUE, label: `Good fit (${t2}%)` },
            { color: ACCENT_ORANGE, label: `Low fit (${t3}%)` },
            { color: ACCENT_RED, label: `Unqualified (${t4}%)` },
          ].map((item) => (
            <Stack key={item.label} direction="row" alignItems="center" spacing={0.6}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: item.color }} />
              <Typography fontFamily={interFont} fontSize={12} color={TEXT_MUTED}>{item.label}</Typography>
            </Stack>
          ))}
        </Stack>
      </Paper>

      {/* Discovery Source */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={5}>
          <Paper
            elevation={0}
            sx={{
              p: "20px 24px",
              borderRadius: "12px",
              background: BG_CARD,
              border: `1px solid ${BORDER}`,
              height: "100%",
            }}
          >
            <Typography fontFamily={interFont} fontSize={15} fontWeight={600} color={TEXT_PRIMARY} mb={2}>
              Discovery Source
            </Typography>
            <BarChart
              bars={data.discovery_source.bars}
              total={data.discovery_source.total}
              date={data.discovery_source.date}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Paper
            elevation={0}
            sx={{
              p: "20px 24px",
              borderRadius: "12px",
              background: BG_CARD,
              border: `1px solid ${BORDER}`,
              height: "100%",
            }}
          >
            <Typography fontFamily={interFont} fontSize={15} fontWeight={600} color={TEXT_PRIMARY} mb={2}>
              Discovery Source Breakdown
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Source", "Leads", "Tier 1", "Tier 2", "Tier 3", "Tier 4"].map((col) => (
                    <TableCell
                      key={col}
                      sx={{
                        fontFamily: interFont,
                        fontSize: 12,
                        color: TEXT_MUTED,
                        borderBottom: `1px solid ${BORDER}`,
                        py: 0.8,
                        px: 1,
                      }}
                    >
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.source_breakdown.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ fontFamily: interFont, fontSize: 12, color: TEXT_PRIMARY, borderBottom: `1px solid ${BORDER}`, py: 1, px: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={0.8}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: row.color, flexShrink: 0 }} />
                        {row.source}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontFamily: interFont, fontSize: 12, color: TEXT_PRIMARY, borderBottom: `1px solid ${BORDER}`, py: 1, px: 1 }}>{row.leads.toLocaleString()}</TableCell>
                    <TableCell sx={{ fontFamily: interFont, fontSize: 12, color: TEXT_PRIMARY, borderBottom: `1px solid ${BORDER}`, py: 1, px: 1 }}>{row.tier1}</TableCell>
                    <TableCell sx={{ fontFamily: interFont, fontSize: 12, color: TEXT_PRIMARY, borderBottom: `1px solid ${BORDER}`, py: 1, px: 1 }}>{row.tier2}</TableCell>
                    <TableCell sx={{ fontFamily: interFont, fontSize: 12, color: TEXT_PRIMARY, borderBottom: `1px solid ${BORDER}`, py: 1, px: 1 }}>{row.tier3}</TableCell>
                    <TableCell sx={{ fontFamily: interFont, fontSize: 12, color: TEXT_PRIMARY, borderBottom: `1px solid ${BORDER}`, py: 1, px: 1 }}>{row.tier4}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>

      {/* Email & LinkedIn Performs */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <PerformsCard
            icon={<EmailOutlinedIcon fontSize="small" />}
            title="Email performs"
            rows={[
              { label: "Email Sent", value: data.email_performs.sent },
              { label: "Opened", value: data.email_performs.opened },
              { label: "Clicked", value: data.email_performs.clicked },
            ]}
            badgeText={data.email_performs.clicked_badge}
            badgeColor="#2A8000"
            badgeBg="#EFFFE8"
            performanceLabel={data.email_performs.performance}
            performanceBadge=""
            performanceBadgeColor=""
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <PerformsCard
            icon={<LinkedInIcon fontSize="small" />}
            title="LinkedIn performs"
            rows={[
              { label: "Sent", value: data.linkedin_performs.sent },
              { label: "Replied", value: data.linkedin_performs.replied },
              { label: "Clicked", value: data.linkedin_performs.clicked },
            ]}
            performanceLabel={data.linkedin_performs.performance}
            performanceBadge={data.linkedin_performs.performance_label}
            performanceBadgeColor={ACCENT_RED}
          />
        </Grid>
      </Grid>
    </Box>
  );
}